'use client';

/**
 * ObeyaCostPlanKpisCard — overview KPI row for a cost plan: total value,
 * vendor count, linked RFQ count, and Reach.
 *
 * Slices the SAME `costplan_details` payload `obeya_costplan` (the
 * mega-card below it on this preset) already fetches — a second card
 * reading one composite response, not a new strategy. `vendor_count`,
 * `reach_count`, `reach_sent_count` are computed server-side
 * (get_costplan_details) rather than derived here: reach is a join
 * across the plan's own lines and every linked RFQ's own line items on
 * product_sid, not a simple count over one already-materialized list.
 *
 * Reach is the number this whole design pass exists to surface — the
 * live investigation earlier this session found it buried (had to be
 * hand-joined in Cypher to answer "does this line's product actually
 * reach a real RFQ, and is that RFQ sent or still draft"). This KPI row
 * puts that answer on screen the moment the plan opens.
 *
 * Labels ride the same `costplan_details` response as the figures —
 * the BFF embeds `_obeya_labels()` directly on its own strategy
 * response, not a second `obeya/list` fetch bolted on for chrome text.
 *
 * Single reading line, not four tiles (real UX feedback: four
 * full-height KPI cards + a separate save/compare row each claimed a
 * whole line before any real line-item data appeared — "we lose the
 * top 20% to admin"). Same figures, same source, just painted as one
 * compact row — mode1_costplan now places this card at w=4 sharing a
 * row with obeya_costplan_versions (w=8) instead of a full-width row
 * of its own. `sub` qualifiers (draft RFQ count, reach-via-sent-RFQ
 * count) still export for scene fidelity but don't render here — no
 * room for a second line at this height, same trade the old tiles
 * already made for their own `sub` line.
 *
 * New-card typography: tokens.ts (`text.*`), not `useCardTypography` —
 * TypographyBundle is deprecated for new cards. Container chrome
 * (padding, border) lives here per the mode7 doctrine.
 *
 * @see klyde_edge/ui_config/composition/cards/obeya.py — content_key="obeya_costplan_kpis"
 * @see klyde_edge/klyde_intelligence/projects/queries.py — get_costplan_details reach/vendor_count
 * @see klyde_ipe/components/copilot/rail-insights/entity-details-card.tsx — the sibling mega-card
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { useMemo } from 'react';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { useStrategyData } from '@/lib/api/hooks';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import { useCardExport, paintedKpi } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';

interface CostPlanKpiData {
  name: string | null;
  amount_total: number | null;
  currency: string | null;
  line_count: number;
  vendor_count: number;
  linked_rfqs: Array<{ status: string | null }>;
  reach_count: number;
  reach_sent_count: number;
  draft_rfq_count: number;
  labels?: Record<string, string>;
}

function money(amount: number | null | undefined, currency: string | null | undefined): string {
  if (amount == null) return '—';
  return `${amount.toLocaleString(undefined, { maximumFractionDigits: 0 })} ${currency ?? ''}`.trim();
}

function Reading({
  label, value, sub, contentKey, queryKey,
}: {
  label: string; value: string; sub?: string;
  contentKey: string; queryKey: readonly unknown[];
}) {
  // paintedKpi enforces the scene-fidelity contract (painted string in
  // `json`, `query_key` required) at the type level. `sub` still
  // exports for scene fidelity even though it doesn't render at this
  // density — the qualifier fact (e.g. "2 via a sent RFQ") is real
  // data, just dropped from the compact reading, not from the export.
  useCardExport(paintedKpi({
    label, value, sub, content_key: contentKey, query_key: queryKey as any,
  }));
  return (
    <span className="whitespace-nowrap">
      <span className={cn(text.value, 'font-semibold')}>{value}</span>{' '}
      <span className={text.muted}>{label}</span>
    </span>
  );
}

export function ObeyaCostPlanKpisCard() {
  const { mcpId } = useViewportSelection();
  const { contentKey } = useCardIdentity();

  const strategy = useCapabilityStore((s) => s.strategyName('cost_plans', 'detail'));
  const lang = useLanguageStore((s) => s.lang);
  const { data: raw, isLoading } = useStrategyData<CostPlanKpiData>(
    strategy, { semantic_id: mcpId ?? '', ...(lang ? { lang } : {}) }, !!mcpId && !!strategy,
  );
  const d = raw?.data;
  // Chrome labels ride the SAME costplan_details payload as the KPI
  // figures — BFF-sourced (_obeya_labels), not a second obeya/list fetch.
  const L = d?.labels ?? {};

  const queryKey = ['strategy-data', strategy, { semantic_id: mcpId ?? '', ...(lang ? { lang } : {}) }] as const;

  // BFF-computed now (get_costplan_details) — was filtered/counted
  // here client-side, same rollup class as reach_count/reach_sent_count,
  // just missed when those were built.
  const draftCount = d?.draft_rfq_count ?? 0;

  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'obeya_costplan_kpis',
    status: (d ? 'loaded' : mcpId && strategy ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: mcpId && strategy ? queryKey : null,
    text: 'Cost plan overview KPIs',
    json: null,
  }), [contentKey, d, mcpId, strategy, queryKey]));

  if (!mcpId) {
    return (
      <div className="flex items-center justify-center h-full text-xs text-muted-foreground">
        Select a cost plan
      </div>
    );
  }

  const val = (v: string) => (isLoading ? '…' : v);

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 h-full text-xs">
      {/* Not ready: most requirements on a real plan now carry several
          competing vendor lines (Line mode groups by requirement, not
          product — see build_costplan_pivot), and this figure was
          summing every alternative as if all were being bought rather
          than settling on one per requirement. Shown as "—" until that
          settling rule exists, rather than a wrong number that looks
          confident. See docs/wip.md. */}
      <Reading
        contentKey="obeya_costplan_kpis:total_value" queryKey={queryKey}
        label={L.kpi_total_value ?? ''}
        value={val('—')}
        sub={d ? 'Not ready — see line-level detail' : undefined}
      />
      <Reading
        contentKey="obeya_costplan_kpis:vendors" queryKey={queryKey}
        label={L.vendors ?? ''}
        value={val(String(d?.vendor_count ?? 0))}
      />
      <Reading
        contentKey="obeya_costplan_kpis:linked_rfqs" queryKey={queryKey}
        label={L.kpi_linked_rfqs ?? ''}
        value={val(String(d?.linked_rfqs?.length ?? 0))}
        sub={d && draftCount > 0 ? `${draftCount} ${L.kpi_draft_unit ?? ''}`.trim() : undefined}
      />
      <Reading
        contentKey="obeya_costplan_kpis:reach" queryKey={queryKey}
        label={L.kpi_reach ?? ''}
        value={val(d ? `${d.reach_count} / ${d.line_count}` : '—')}
        sub={d && d.reach_count > 0 ? `${d.reach_sent_count} ${L.kpi_reach_sent_unit ?? ''}`.trim() : undefined}
      />
    </div>
  );
}
